import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { combineReducers, createStore } from 'redux';

import 'animate.css';
import 'hint.css';
import { AppState } from 'Redux/AppState';
import './index.css';

import { AppContextProvider } from 'Context/app.context';
import { CurrentUserContextProvider } from 'Context/current-user.context';

import { AppMode } from 'Common/Enums/metadata.enum';
import { isMobile, isOpenInsideIframe } from 'Common/Utils/CheckDevice';
import RenderLoader from 'Components/FullPage/RenderLoader/render-loader.component';
import AuthComponent from 'Components/FunctionalComponents/Auth/auth.component';
import LocalizationComponent from 'Components/FunctionalComponents/Localization/localization.component';
import ErrorBoundary from 'Components/Reusable/ErrorBoundary/error-boundary';
import {
    AppTypeEnum,
    CurrentAppContext,
    MobileViewEnum,
} from 'Context/current-app.context';
import { FilterProvider } from 'Context/current-filter.context';
import { FilePreviewProvider } from 'Context/file-preview.context';
import { NotificationSoundProvider } from 'Context/notification-sound.context';
import FullPage from 'EntryPoints/full-page.web';
import RecentContactsReducer from 'Redux/Reducers/recent-contacts.reducer';
import { remotesConfigs } from './remotesConfigs';
import Widget from 'EntryPoints/widget';
import { initCoralogix } from 'monitoring/coralogix';
import { checkScriptLoader } from 'utils/script-loader';

initCoralogix('fullscreen');

(window as any).logger = (function () {
    let oldConsoleLog: any = null;
    const pub = {} as any;

    pub.enableLogger = function enableLogger() {
        if (oldConsoleLog == null) return;

        window.console.log = oldConsoleLog;
    };

    pub.disableLogger = function disableLogger() {
        oldConsoleLog = console.log;
        window.console.log = function () {};
    };

    return pub;
})();

// console.log("Disable Logger");
(window as any).logger.disableLogger();

const Ding = require('Assets/Audio/Message-notification.mp3'); // eslint-disable-line @typescript-eslint/no-var-requires

const rootReducer = combineReducers<AppState>({
    recentContacts: RecentContactsReducer,
});

const store = createStore(
    rootReducer,
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
        (window as any).__REDUX_DEVTOOLS_EXTENSION__() // eslint-disable-line no-underscore-dangle
);

const App = () => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const isScriptLoaderLoaded = await checkScriptLoader();
            if (!isScriptLoaderLoaded) {
                setLoading(false);
                return;
            }
            const { registerScripts, registerStylesheets } = (window as any)
                .ScriptLoader;
            registerScripts(
                remotesConfigs.filter((config) => config.type === 'script')
            );
            registerStylesheets(
                remotesConfigs.filter((config) => config.type === 'stylesheet')
            );
            setLoading(false);
        })();
    }, []);

    if (loading) {
        return null;
    }

    return (
        <React.StrictMode>
            <audio id="cv-notification-sound">
                <source src={Ding} type="audio/mp3" />
            </audio>
            <ErrorBoundary currentApp={AppTypeEnum.Fullscreen}>
                <AppContextProvider>
                    <RenderLoader />
                    <CurrentAppContext.Provider
                        value={{
                            appType: AppTypeEnum.Fullscreen,
                            isMobile: isMobile(),
                            mobileView: isMobile()
                                ? isOpenInsideIframe()
                                    ? MobileViewEnum.Iframe
                                    : window?.location?.origin?.includes(
                                            'converse-widget'
                                        )
                                      ? MobileViewEnum.WebView
                                      : MobileViewEnum.None
                                : MobileViewEnum.None,
                            isOpenInsideIframe: isOpenInsideIframe(),
                        }}
                    >
                        <AuthComponent mode={AppMode.FullScreen}>
                            <LocalizationComponent>
                                <CurrentUserContextProvider>
                                    <FilterProvider>
                                        <Provider store={store}>
                                            <NotificationSoundProvider>
                                                <FilePreviewProvider>
                                                    <FullPage />
                                                </FilePreviewProvider>
                                            </NotificationSoundProvider>
                                        </Provider>
                                    </FilterProvider>
                                </CurrentUserContextProvider>
                            </LocalizationComponent>
                        </AuthComponent>
                    </CurrentAppContext.Provider>
                </AppContextProvider>
            </ErrorBoundary>
        </React.StrictMode>
    );
};

ReactDOM.render(<App />, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
